import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import CMSNav from "./nav";
import Spinner from "../../components/Spinner";
import "./ShowProducts.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.goharesadaf.ir";

function GalleryModal({ images = [], startIndex = 0, open, onClose }) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    setIndex(startIndex);
  }, [startIndex, images]);

  const prev = () => setIndex((i) => (i <= 0 ? images.length - 1 : i - 1));
  const next = () => setIndex((i) => (i >= images.length - 1 ? 0 : i + 1));

  const onKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape") onClose();
    },
    [open, onClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  if (!open) return null;

  const img = images[index];

  return (
    <div className="gallery-overlay" onClick={onClose}>
      <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
        <button className="gallery-close" onClick={onClose} aria-label="close">✕</button>

        <div className="gallery-main" onClick={next}>
          {img ? (
            <img src={img} alt={`image-${index}`} />
          ) : (
            <div className="gallery-empty">تصويري وجود ندارد</div>
          )}
        </div>

        <div className="gallery-controls">
          <button onClick={prev} className="gallery-btn">▶</button>
          <div className="gallery-counter">{images.length} / {index + 1}  </div>
          <button onClick={next} className="gallery-btn">◀</button>
        </div>

        {images.length > 1 && (
          <div className="gallery-thumbs">
            {images.map((src, i) => (
              <button
                key={i}
                className={`thumb ${i === index ? "active" : ""}`}
                onClick={() => setIndex(i)}
                aria-label={`show ${i + 1}`}
              >
                <img src={src} alt={`thumb-${i}`} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // edit modal states
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({});
  const [existingImages, setExistingImages] = useState([]); // { url }
  const [removedImages, setRemovedImages] = useState([]); // array of urls/paths to delete
  const [newFiles, setNewFiles] = useState([]); // File[]
  const [newPreviews, setNewPreviews] = useState([]); // { url, fileIndex }
  const [mainImageChoice, setMainImageChoice] = useState({ type: "existing", index: 0 }); // {type: 'existing'|'new', index}
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // gallery state
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line
  }, [token]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/admin/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(res.data);
    } catch (err) {
      console.error("Error fetching products:", err);
      toast.error("Failed to fetch products.");
    } finally {
      setLoading(false);
    }
  };

  const openGalleryForProduct = (product, startIndex = 0) => {
    // product.images assumed to be array of full URLs (ProductController formatProduct should ensure that)
    const imgs = product.images && product.images.length ? product.images : [];
    setGalleryImages(imgs);
    setGalleryStartIndex(startIndex);
    setGalleryOpen(true);
  };


  const handleExportExcel = async () => {
    if (!window.confirm("آیا می‌خواهید خروجی Excel از محصولات دریافت کنید؟")) return;

    try {
      const res = await axios.get(`${API_BASE}/api/admin/products/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob", // important for file downloads
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "products.xlsx"); // file name
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Excel export started!");
    } catch (err) {
      console.error("Error exporting products:", err);
      toast.error("خطا در خروجی Excel");
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm("آیا مطمئن هستید که می‌خواهید این محصول را حذف کنید؟")) return;
    try {
      await axios.delete(`${API_BASE}/api/admin/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((p) => p.filter((x) => x.id !== id));
      toast.success("Product deleted successfully!");
    } catch (err) {
      console.error("Error deleting product:", err);
      toast.error("خطا در حذف محصول");
    }
  };

  const handleEditClick = (product) => {
    setEditingProduct(product);
    setFormData({
      id: product.id,
      title: product.title ?? "",
      slug: product.slug ?? "",
      description: product.description ?? "",
      price: product.price ?? "",
      stock: product.stock ?? 0,
      color: product.color ?? "",
      dimensions: product.dimensions ?? "",
      material: product.material ?? "",
      discount_percentage: product.discount_percentage ?? "",
      on_sale: product.on_sale ?? "",
      category_id: product.category_id ?? null,
      active: product.active ?? false,
      main_image_file: null,
    });

    const imgs = (product.images || []).map((url) => ({ url }));
    setExistingImages(imgs);
    setRemovedImages([]);
    setNewFiles([]);
    setNewPreviews([]);

    if (product.main_image) {
      const idx = imgs.findIndex((img) => {
        if (img.url === product.main_image) return true;
        return img.url && product.main_image && img.url.includes(product.main_image);
      });
      setMainImageChoice({ type: "existing", index: idx >= 0 ? idx : 0 });
    } else {
      setMainImageChoice({ type: "existing", index: 0 });
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleMainFileChange = (e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    setFormData((prev) => ({ ...prev, main_image_file: file }));
    const idx = newFiles.length;
    setNewFiles((prev) => [...prev, file]);
    const previewUrl = URL.createObjectURL(file);
    setNewPreviews((prev) => [...prev, { url: previewUrl, fileIndex: idx }]);
    setMainImageChoice({ type: "new", index: idx });
  };

  const handleNewMultipleChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const startIndex = newFiles.length;
    setNewFiles((prev) => [...prev, ...files]);
    const previews = files.map((f, i) => ({ url: URL.createObjectURL(f), fileIndex: startIndex + i }));
    setNewPreviews((prev) => [...prev, ...previews]);
    if (existingImages.length === 0 && (newFiles.length + files.length) > 0) {
      setMainImageChoice({ type: "new", index: startIndex });
    }
  };

  const handleRemoveExistingImage = (idx) => {
    const img = existingImages[idx];
    if (!img) return;
    if (!window.confirm("آیا از حذف این تصویر مطمئن هستید؟ این عملیات دائمی است.")) return;
    setRemovedImages((prev) => [...prev, img.url]);
    setExistingImages((prev) => prev.filter((_, i) => i !== idx));

    if (mainImageChoice.type === "existing") {
      if (mainImageChoice.index === idx) {
        if (newPreviews.length > 0) {
          setMainImageChoice({ type: "new", index: 0 });
        } else if (existingImages.length > 1) {
          setMainImageChoice({ type: "existing", index: 0 });
        } else {
          setMainImageChoice({ type: "existing", index: 0 });
        }
      } else if (mainImageChoice.index > idx) {
        setMainImageChoice((prev) => ({ ...prev, index: prev.index - 1 }));
      }
    }
  };

  const handleRemoveNewPreview = (fileIndex) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== fileIndex));
    setNewPreviews((prev) => prev.filter((p) => p.fileIndex !== fileIndex));
    setTimeout(() => {
      setNewPreviews((prev) => prev.map((p, i) => ({ ...p, fileIndex: i })));
      setNewFiles((prev) => prev);
    }, 0);

    if (mainImageChoice.type === "new") {
      if (mainImageChoice.index === fileIndex) {
        if (newPreviews.length > 1) setMainImageChoice({ type: "new", index: 0 });
        else if (existingImages.length > 0) setMainImageChoice({ type: "existing", index: 0 });
        else setMainImageChoice({ type: "existing", index: 0 });
      } else if (mainImageChoice.index > fileIndex) {
        setMainImageChoice((prev) => ({ ...prev, index: prev.index - 1 }));
      }
    }
  };

  const handleSelectMain = (choice) => setMainImageChoice(choice);

  useEffect(() => {
    return () => newPreviews.forEach((p) => URL.revokeObjectURL(p.url));
  }, [newPreviews]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const fd = new FormData();
      const simpleFields = ["title", "slug", "price", "stock", "description", "color", "dimensions", "material", "category_id", "active", "discount_percentage", "on_sale"];
      simpleFields.forEach((key) => {
        if (formData[key] !== undefined) fd.append(key, formData[key]);
      });

      newFiles.forEach((file) => fd.append("images[]", file));
      fd.append("main_image_choice_type", mainImageChoice.type);
      fd.append("main_image_choice_index", String(mainImageChoice.index));
      removedImages.forEach((url) => fd.append("removed_images[]", url));
      fd.append("_method", "PUT");

      // DON'T set Content-Type manually; axios will set the boundary
      await axios.post(`${API_BASE}/api/admin/products/${editingProduct.id}`, fd, {
        headers: { Authorization: `Bearer ${token}` },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        },
      });

      toast.success("Product updated successfully!");
      newPreviews.forEach((p) => URL.revokeObjectURL(p.url));
      setNewPreviews([]);
      setNewFiles([]);
      setRemovedImages([]);
      setEditingProduct(null);
      fetchProducts();
    } catch (err) {
      console.error("Error updating product:", err);
      toast.error(err.response?.data?.message || "خطا در بروزرسانی محصول");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };


  return (
    <div className="products-page">
      <CMSNav />
    {loading && <Spinner />}
    {error && <p>خطا در بارگذاری: {error}</p>}

      <h1>فهرست محصولات:</h1>
      <div style={{ margin: "10px 0" }}>
        <button className="export-btn" onClick={handleExportExcel}>📥 Export to Excel</button>
      </div>

      {/* Gallery modal */}
      <GalleryModal
        images={galleryImages}
        startIndex={galleryStartIndex}
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />

      {editingProduct && (
        <div className="edit-form">
          <h2>ویرایش محصول: {editingProduct.title}</h2>
          <form onSubmit={handleUpdate}>
            <div className="form-row">
              <input type="text" name="title" value={formData.title || ""} onChange={handleInputChange} placeholder="نام محصول" />
              <input type="text" name="slug" value={formData.slug || ""} onChange={handleInputChange} placeholder="اسلاگ" />
              <input type="text" name="description" value={formData.description || ""} onChange={handleInputChange} placeholder="توضیحات" />
              <input type="number" name="price" value={formData.price || ""} onChange={handleInputChange} placeholder="قیمت" />
              <input type="number" name="stock" value={formData.stock || ""} onChange={handleInputChange} placeholder="تعداد" />
            </div>
            <div className="form-row">
              <input type="number"
                    name="discount_percentage"
                    min="0" max="100"
                    value={formData.discount_percentage ?? 0}
                    onChange={handleInputChange}
                    placeholder="درصد تخفیف" />

              <label style={{display: 'inline-flex', alignItems: 'center', gap: 6}}>
                <input type="checkbox"
                      name="on_sale"
                      checked={!!formData.on_sale}
                      onChange={handleInputChange} />
                فعال در فروش ویژه
              </label>
            </div>
            <div className="form-row">
              <input type="text" name="color" value={formData.color || ""} onChange={handleInputChange} placeholder="رنگ" />
              <input type="text" name="dimensions" value={formData.dimensions || ""} onChange={handleInputChange} placeholder="ابعاد" />
              <input type="text" name="material" value={formData.material || ""} onChange={handleInputChange} placeholder="جنس" />
            </div>

            <hr />

            <div style={{ margin: "10px 0" }}>
              <p>تصاویر فعلی:</p>
              {existingImages.length === 0 && <p style={{ color: "#777" }}>هیچ تصویری وجود ندارد.</p>}
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                {existingImages.map((img, idx) => (
                  <div key={idx} style={{ position: "relative", width: 120 }}>
                    <img src={img.url} alt={`img-${idx}`} style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 6 }} />
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button type="button" onClick={() => handleRemoveExistingImage(idx)} className="mini-btn danger">حذف</button>
                      <button type="button" onClick={() => handleSelectMain({ type: "existing", index: idx })} className={`mini-btn ${mainImageChoice.type === "existing" && mainImageChoice.index === idx ? "active" : ""}`}>
                        {mainImageChoice.type === "existing" && mainImageChoice.index === idx ? "اصلی ✅" : "انتخاب به عنوان اصلی"}
                      </button>
                      <button type="button" onClick={() => { setGalleryImages(existingImages.map(i => i.url)); setGalleryStartIndex(idx); setGalleryOpen(true); }} className="mini-btn">نمایش بزرگ</button>
                    </div>
                  </div>
                ))}
              </div>

              <hr style={{ margin: "12px 0" }} />

              <div>
                <label>
                  بارگذاری تصویر اصلی جدید (اختیاری):
                  <input type="file" accept="image/*" onChange={handleMainFileChange} />
                </label>
              </div>

              <div style={{ marginTop: 8 }}>
                <label>
                  بارگذاری تصاویر اضافی جدید:
                  <input type="file" accept="image/*" multiple onChange={handleNewMultipleChange} />
                </label>
              </div>

              {newPreviews.length > 0 && (
                <>
                  <p className="new-previews-label">پیش‌نمایش تصاویر جدید:</p>
                  <div className="new-previews-container">
                    {newPreviews.map((p) => (
                      <div key={p.fileIndex} className="new-preview-wrapper">
                        <img
                          src={p.url}
                          alt={`new-${p.fileIndex}`}
                          className="new-preview-img"
                        />
                        <div className="new-preview-actions">
                          <button
                            type="button"
                            onClick={() => handleRemoveNewPreview(p.fileIndex)}
                            className="mini-btn danger"
                          >
                            حذف
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              handleSelectMain({ type: "new", index: p.fileIndex })
                            }
                            className={`mini-btn ${
                              mainImageChoice.type === "new" &&
                              mainImageChoice.index === p.fileIndex
                                ? "active"
                                : ""
                            }`}
                          >
                            {mainImageChoice.type === "new" &&
                            mainImageChoice.index === p.fileIndex
                              ? "اصلی ✅"
                              : "انتخاب به عنوان اصلی"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}


            </div>

            <div style={{ marginTop: "10px" }}>
              <button className="save-btn" type="submit" disabled={uploading}>
                {uploading ? `در حال آپلود... ${uploadProgress}%` : "ذخیره تغییرات"}
              </button>
              <button className="cancel-btn" type="button" onClick={() => setEditingProduct(null)} disabled={uploading}>
                انصراف
              </button>
            </div>
          </form>
        </div>
      )}

      {products.length === 0 ? (
        <p>هیچ محصولی یافت نشد.</p>
      ) : (
        <table className="products-table">
          <thead>
            <tr>
              <th>شماره</th>
              <th>نام محصول</th>
              <th>اسلاگ</th>
              <th>قیمت</th>
              <th>تعداد</th>
              <th>دسته‌بندی</th>
              <th>موجود</th>
              <th>رنگ</th>
              <th>ابعاد</th>
              <th>جنس</th>
              <th>فروش ویژه</th>
              <th>درصد تخفیف</th>
              <th>تصویر</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id}>
                <td>{p.id}</td>
                <td>{p.title}</td>
                <td>{p.slug}</td>
                <td>{p.price}</td>
                <td>{p.stock}</td>
                <td>{p.category_id ?? "—"}</td>
                <td>{p.active ? "✅" : "❌"}</td>
                <td>{p.color}</td>
                <td>{p.dimensions}</td>
                <td>{p.material}</td>
                <td>{p.on_sale ? "✅" : "❌"}</td>
                <td>{p.discount_percentage}</td>

                <td>
                  <img
                    src={p.main_image ? (p.main_image.startsWith("https") ? p.main_image : `${API_BASE}/storage/${p.main_image}`) : (p.images?.[0] || "")}
                    alt={p.title}
                    style={{ width: "50px", height: "50px", objectFit: "cover", cursor: "pointer" }}
                    onClick={() => openGalleryForProduct(p, 0)}
                  />
                </td>
                <td>
                  <button className="action-btn edit-btn" onClick={() => handleEditClick(p)}>✏️ ویرایش</button>
                  <button className="action-btn show-images" onClick={() => openGalleryForProduct(p, 0)}>🖼️ تصاویر</button>
                  <button className="action-btn delete-btn" onClick={() => handleDelete(p.id)}>🗑️ حذف</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProductsPage;